/**
 * DAD LINK — Child's calling surface, overlay v2 (operator spec 2026-07-11).
 *
 * THE PROCESS (staged, not a slideshow):
 *   1. ENCRYPTED LINK — "creating encrypted link to the cell tower network,
 *      avoiding detection" + progress bar.
 *   2. SATELLITE ACCESS QUEUE — satellites flip IN QUEUE → LINKED.
 *   3. SCANNING — camera ZOOMS IN on regional towns one by one, checks, zooms out,
 *      moves on (tour order + timings randomized per call so it never repeats).
 *   4. On answer: the tour stops, the camera flies to Dad's location and
 *      "DAD FOUND" BLINKS. Then the voice HUD.
 *   Missed/declined → DAD NOT FOUND, orange, callback beacon.
 *
 * All HUD text sits on scrims — nothing clips over map labels (v1 bug).
 * WebRTC: audio-first, p2p over the tailnet, diag reports to the server log.
 */
import { useEffect, useRef, useState } from "react";
import { createDadLinkAudio, type DadLinkAudio } from "./dadlinkSounds";

// SETUP: replace these with towns from YOUR region. Coordinates are fractions
// (0..1) of the map image. TARGET = your (Dad's) home town — the "DAD FOUND"
// location. ORIGIN = where the child's tablet lives. TOUR_POOL = decoy towns the
// "satellite" scans past (must be names that exist in TOWNS, not TARGET/ORIGIN).
// The names below are FICTIONAL placeholders — nothing here should reveal a real
// location. See SETUP.md.
const TOWNS = [
  { n: "NORTHGATE", x: 0.075, y: 0.155 },
  { n: "IRONWOOD", x: 0.165, y: 0.175 },
  { n: "CEDAR BEND", x: 0.225, y: 0.245 },
  { n: "PORT HAVEN", x: 0.285, y: 0.205 },
  { n: "CHESTNUT HILL", x: 0.36, y: 0.205 },
  { n: "LAKESHORE CITY", x: 0.53, y: 0.13 },
  { n: "MAPLE RIDGE", x: 0.185, y: 0.29 },
  { n: "CROWN HOLLOW", x: 0.175, y: 0.36 },
  { n: "SUMMITVILLE", x: 0.385, y: 0.335 },
  { n: "WESTFORD", x: 0.52, y: 0.43 },
  { n: "HAVENBROOK", x: 0.275, y: 0.49 },
  { n: "KELSO", x: 0.405, y: 0.52 },
  { n: "DELMONT", x: 0.255, y: 0.66 },
  { n: "WHEATON HOLLOW", x: 0.36, y: 0.72 },
  { n: "RIDGEFIELD", x: 0.32, y: 0.885 },
];
const ORIGIN = TOWNS[13]; // SETUP: child's location
const TARGET = TOWNS[8];   // SETUP: Dad's home town ("DAD FOUND")
const TOUR_POOL = ["IRONWOOD", "CROWN HOLLOW", "LAKESHORE CITY", "HAVENBROOK", "KELSO", "DELMONT", "CHESTNUT HILL", "MAPLE RIDGE", "WESTFORD"];

const SATS = [
  { name: "KEENBEAN-1", detail: "PRIVATE UPLINK" },
  { name: "GOES-MIDWEST", detail: "WEATHER GRID" },
  { name: "LOW-ORBIT RELAY 7", detail: "SIGNAL BOUNCE" },
];

type Phase = "ring" | "found" | "video" | "lost";

function makeProfile() {
  const shuffle = <T,>(a: T[]) => [...a].sort(() => Math.random() - 0.5);
  return {
    // A LONG random walk, not a short cycle — every zoom target is freshly drawn
    // (no adjacent repeats), so even a 90s ring never shows the same sequence twice.
    tour: (() => {
      const seq: typeof TOWNS = [] as any;
      let prev = "";
      for (let i = 0; i < 60; i++) {
        const pick = shuffle(TOUR_POOL.filter((n) => n !== prev))[0];
        prev = pick;
        seq.push(TOWNS.find((t) => t.n === pick)!);
      }
      return seq;
    })(),
    dwell: 2.4 + Math.random() * 0.9, // seconds zoomed on each town
    breathe: 1.3 + Math.random() * 0.5, // seconds at overview between towns
    encryptSecs: 3.2 + Math.random() * 1.2,
    satSecs: 1.1 + Math.random() * 0.5, // per satellite
    pingRate: 0.4 + Math.random() * 0.25,
  };
}

const MONO = '"Cascadia Mono", Consolas, "SF Mono", ui-monospace, Menlo, monospace';
const SCRIM = "rgba(6, 10, 16, 0.82)";

const CSS = `
.dadlink-overlay { position: absolute; inset: 0; z-index: 60; background: #06090f; font-family: ${MONO}; animation: dl-fade 0.4s ease both; overflow: hidden; }
@keyframes dl-fade { from { opacity: 0; } }
.dadlink-overlay canvas { position: absolute; inset: 0; width: 100%; height: 100%; }
.dl-head { position: absolute; top: 4.5%; left: 50%; transform: translateX(-50%); padding: 8px 26px; background: ${SCRIM}; border: 1px solid rgba(126,224,255,0.18);
  font-size: clamp(24px, 3.8vw, 50px); letter-spacing: 0.3em; text-indent: 0.3em; font-weight: 700; color: #7ee0ff; text-shadow: 0 0 26px rgba(126,224,255,0.55); white-space: nowrap; }
.dl-sub { position: absolute; top: calc(4.5% + clamp(48px, 6.4vw, 84px)); left: 50%; transform: translateX(-50%); padding: 4px 16px; background: ${SCRIM};
  font-size: clamp(9px, 1vw, 13px); letter-spacing: 0.24em; color: rgba(126,224,255,0.55); white-space: nowrap; }
.dl-head.blink { animation: dl-foundblink 0.55s steps(1) infinite; color: #eaffff; text-shadow: 0 0 40px rgba(126,224,255,1); }
@keyframes dl-foundblink { 50% { opacity: 0.18; } }
.dadlink-overlay.lost .dl-head { color: #ff6b00; text-shadow: 0 0 26px rgba(255,107,0,0.55); }
.dadlink-overlay.lost .dl-sub { color: rgba(255,107,0,0.7); }

/* boot process panel (stages 1-2) */
.dl-boot { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(560px, 84vw); background: ${SCRIM};
  border: 1px solid rgba(126,224,255,0.25); padding: 22px 26px; text-align: left; }
.dl-boot h3 { margin: 0 0 12px; font-size: clamp(12px, 1.3vw, 16px); letter-spacing: 0.22em; color: #7ee0ff; font-weight: 600; }
.dl-boot .line { font-size: clamp(10px, 1.05vw, 13px); letter-spacing: 0.1em; line-height: 2; color: rgba(126,224,255,0.6); }
.dl-boot .line.done::before { content: "✓ "; color: #7ee0ff; }
.dl-boot .line.active::before { content: "▸ "; color: #7ee0ff; }
.dl-bar { margin-top: 14px; height: 10px; border: 1px solid rgba(126,224,255,0.35); background: rgba(126,224,255,0.06); }
.dl-bar i { display: block; height: 100%; background: #7ee0ff; box-shadow: 0 0 14px rgba(126,224,255,0.7); transition: width 0.28s linear; }
.dl-sat { display: flex; justify-content: space-between; gap: 12px; font-size: clamp(10px, 1.05vw, 13px); letter-spacing: 0.12em; line-height: 2.1; }
.dl-sat .st { color: rgba(126,224,255,0.4); }
.dl-sat.linked .st { color: #06090f; background: #7ee0ff; padding: 0 8px; font-weight: 700; }
.dl-sat.req .st { color: #eaffff; animation: dl-foundblink 0.6s steps(1) infinite; }

/* scan chip (bottom-left, scrimmed) */
.dl-scan { position: absolute; left: 3%; bottom: 5.5%; padding: 10px 18px; background: ${SCRIM}; border: 1px solid rgba(126,224,255,0.22);
  font-size: clamp(10px, 1.1vw, 14px); letter-spacing: 0.16em; color: #7ee0ff; text-align: left; line-height: 1.9; }
.dl-scan .dim { color: rgba(126,224,255,0.45); }
.dl-conf { position: absolute; right: 3%; bottom: 5.5%; padding: 8px 16px; background: ${SCRIM}; border: 1px solid rgba(126,224,255,0.22); text-align: right; font-variant-numeric: tabular-nums; }
.dl-conf .n { font-size: clamp(24px, 3.2vw, 44px); font-weight: 700; color: #7ee0ff; }
.dl-conf .l { font-size: clamp(8px, 0.85vw, 11px); letter-spacing: 0.26em; color: rgba(126,224,255,0.45); }

.dl-ring-stage { position: absolute; left: 50%; transform: translateX(-50%); bottom: 5.5%; display: flex; gap: 12px; }
.dl-chip { border: 1px solid rgba(126,224,255,0.35); padding: 8px 16px; font-size: clamp(9px, 0.95vw, 12px); letter-spacing: 0.2em; color: rgba(126,224,255,0.5); background: ${SCRIM}; }
.dl-chip.on { color: #06090f; background: #7ee0ff; border-color: #7ee0ff; animation: dl-ringpulse 0.9s ease-in-out infinite; }
@keyframes dl-ringpulse { 50% { box-shadow: 0 0 22px rgba(126,224,255,0.8); } }
.dl-beacon { position: absolute; left: 50%; transform: translateX(-50%); bottom: 13%; display: flex; align-items: center; gap: 12px; border: 1px solid rgba(255,107,0,0.5); padding: 10px 18px; background: rgba(12,6,2,0.88); font-size: clamp(9px, 1vw, 12px); letter-spacing: 0.2em; color: #ff6b00; }
.dl-beacon i { width: 10px; height: 10px; border-radius: 50%; background: #ff6b00; animation: dl-blink 0.85s steps(1) infinite; box-shadow: 0 0 14px rgba(255,107,0,0.9); }
@keyframes dl-blink { 50% { opacity: 0.12; } }
.dl-end { position: absolute; left: 50%; transform: translateX(-50%); bottom: 1.2%; z-index: 3; background: ${SCRIM}; border: 1px solid rgba(126,224,255,0.3); color: rgba(126,224,255,0.8); font-family: inherit; font-size: clamp(10px, 1vw, 13px); letter-spacing: 0.24em; padding: 10px 28px; cursor: pointer; }
.dl-timer { position: absolute; right: 3%; top: 4.5%; padding: 8px 18px; background: ${SCRIM}; border: 1px solid rgba(126,224,255,0.22); font-size: clamp(18px, 2.4vw, 30px); font-variant-numeric: tabular-nums; color: #eaffff; }

/* EMERGENCY CALL BUTTON — deliberately prominent and ALWAYS available (no rank gate):
   this is the child's direct, one-tap line to Papa. WiFi/data only, WebRTC-locked to
   Papa alone (it can reach no one else). Big pill, top-center, gentle attention pulse. */
.dadlink-tile { position: absolute; top: max(10px, env(safe-area-inset-top)); right: 12px; z-index: 30; background: linear-gradient(180deg, rgba(0,212,255,0.26), rgba(0,140,190,0.20)); border: 1.5px solid rgba(126,224,255,0.9); color: #eafcff; font-family: ${MONO}; font-size: 13px; font-weight: 700; letter-spacing: 0.12em; padding: 10px 18px; border-radius: 999px; cursor: pointer; display: flex; align-items: center; gap: 9px; box-shadow: 0 0 20px rgba(0,212,255,0.4); animation: dl-emgpulse 2.4s ease-in-out infinite; -webkit-tap-highlight-color: transparent; white-space: nowrap; }
.dadlink-tile:active { transform: scale(0.96); }
.dadlink-tile .dl-emg-ico { font-size: 19px; line-height: 1; }
@keyframes dl-emgpulse { 0%,100% { box-shadow: 0 0 20px rgba(0,212,255,0.32); } 50% { box-shadow: 0 0 30px rgba(0,212,255,0.6); } }
`;

export function DadLinkTile({ onOpen }: { onOpen: () => void; locked?: boolean }) {
  // Always active by design — a child must ALWAYS be able to reach Papa in one tap.
  return (
    <>
      <style>{CSS}</style>
      <button className="dadlink-tile" onClick={onOpen} aria-label="Appeler Papa">
        <span className="dl-emg-ico">📞</span> APPELER PAPA
      </button>
    </>
  );
}

export default function DadLinkOverlay({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>("ring");
  const [ringStage, setRingStage] = useState<"desk" | "phone">("desk");
  const [boot, setBoot] = useState<{ stage: 0 | 1 | 2; pct: number; sats: ("queue" | "req" | "linked")[] }>({ stage: 0, pct: 0, sats: ["queue", "queue", "queue"] });
  const [callSecs, setCallSecs] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  const scanRef = useRef<HTMLDivElement>(null);
  const confRef = useRef<HTMLDivElement>(null);
  const phaseRef = useRef<Phase>("ring");
  const bootRef = useRef(boot);
  phaseRef.current = phase;
  bootRef.current = boot;

  // ── soundscape (operator 2026-07-11): power-on sweeps, satellite blips, a
  // continuous sonar ping while ringing, and found/lost resolution tones.
  // Synthesized on-device; driven purely by the state transitions below.
  const sfx = useRef<DadLinkAudio | null>(null);
  if (!sfx.current) sfx.current = createDadLinkAudio();
  useEffect(() => () => sfx.current?.dispose(), []);
  useEffect(() => {
    sfx.current?.powerUp(boot.stage); // fires at mount (stage 0) and each advance
  }, [boot.stage]);
  const linkedCount = boot.sats.filter((s) => s === "linked").length;
  useEffect(() => {
    if (linkedCount > 0) sfx.current?.satBlip();
  }, [linkedCount]);
  useEffect(() => {
    if (phase === "ring") sfx.current?.startRing();
    else sfx.current?.stopRing();
    if (phase === "found") sfx.current?.found();
    if (phase === "lost") sfx.current?.lost();
  }, [phase]);

  useEffect(() => {
    const profile = makeProfile();
    let ws: WebSocket | null = null;
    let pc: RTCPeerConnection | null = null;
    let stream: MediaStream | null = null;
    let raf = 0;
    let dead = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (fn: () => void, ms: number) => { const h = setTimeout(() => { if (!dead) fn(); }, ms); timers.push(h); return h; };
    const t0 = performance.now();
    const now = () => (performance.now() - t0) / 1000;
    let foundAt = 0;
    const send = (o: any) => { if (ws && ws.readyState === 1) ws.send(JSON.stringify(o)); };
    const diag = (o: any) => send(Object.assign({ type: "diag" }, o));

    // ── THE PROCESS: stage 1 encrypted link, stage 2 satellite queue ──
    const encryptMs = profile.encryptSecs * 1000;
    const pctIv = setInterval(() => {
      if (dead) return;
      setBoot((b) => (b.stage === 0 ? { ...b, pct: Math.min(100, b.pct + 4 + Math.random() * 9) } : b));
    }, encryptMs / 22);
    timers.push(pctIv as any);
    later(() => {
      setBoot((b) => ({ ...b, stage: 1, pct: 100 }));
      SATS.forEach((_, i) => {
        later(() => setBoot((b) => { const sats = [...b.sats]; sats[i] = "req"; return { ...b, sats }; }), i * profile.satSecs * 1000 + 150);
        later(() => setBoot((b) => { const sats = [...b.sats]; sats[i] = "linked"; return { ...b, sats }; }), (i + 0.85) * profile.satSecs * 1000);
      });
      later(() => setBoot((b) => ({ ...b, stage: 2 })), SATS.length * profile.satSecs * 1000 + 400);
    }, encryptMs);

    const cleanup = () => {
      dead = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      clearInterval(pctIv);
      if (pc) { try { pc.close(); } catch {} pc = null; }
      if (stream) { stream.getTracks().forEach((t) => t.stop()); stream = null; }
      if (ws) { try { ws.close(); } catch {} ws = null; }
    };

    const toLost = () => { if (phaseRef.current !== "lost") { setPhase("lost"); later(onClose, 12000); } };

    async function begin() {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
      ws = new WebSocket(apiBase.replace(/^http/, "ws") + "/dadlink-ws?role=child&device=tablet");
      ws.onopen = () => send({ type: "call" });
      ws.onclose = () => { if (!dead && phaseRef.current !== "lost") toLost(); };
      ws.onmessage = async (ev) => {
        let m: any; try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === "ring-stage" && m.stage === "phone") setRingStage("phone");
        else if (m.type === "accepted") {
          foundAt = now();
          setPhase("found");
          later(() => setPhase("video"), 2600);
          const iv = setInterval(() => { if (!dead && foundAt) setCallSecs(Math.floor(now() - foundAt)); }, 500);
          timers.push(iv as any);
          pc = new RTCPeerConnection({
            // Public STUN (free) for NAT traversal now that there is no tailnet. Same-WiFi
            // uses host candidates; STUN covers most home-router cases. (A TURN relay would
            // be needed for the hardest cellular/symmetric-NAT case — added later if needed.)
            iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }],
          });
          pc.oniceconnectionstatechange = () => diag({ ice: pc!.iceConnectionState });
          pc.onconnectionstatechange = () => diag({ conn: pc!.connectionState });
          if (stream) stream.getTracks().forEach((t) => pc!.addTrack(t, stream!));
          else pc.addTransceiver("audio", { direction: "recvonly" });
          pc.ontrack = (e) => {
            if (remoteRef.current) {
              remoteRef.current.srcObject = e.streams[0];
              remoteRef.current.play().catch((err) => diag({ playError: String(err).slice(0, 120) }));
            }
            diag({ remoteTrack: e.track.kind });
          };
          pc.onicecandidate = (e) => { if (e.candidate) send({ type: "rtc", payload: { ice: e.candidate } }); };
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          send({ type: "rtc", payload: { offer } });
        } else if (m.type === "rtc" && pc) {
          if (m.payload.answer) await pc.setRemoteDescription(m.payload.answer);
          else if (m.payload.ice) { try { await pc.addIceCandidate(m.payload.ice); } catch {} }
        } else if (m.type === "missed") toLost();
        else if (m.type === "call-ended") { if (phaseRef.current === "video" || phaseRef.current === "found") onClose(); else toLost(); }
      };
    }
    begin();

    // ── canvas: camera-driven map ──
    const cv = canvasRef.current!;
    const ctx = cv.getContext("2d")!;
    let W = 0, H = 0;
    const resize = () => {
      const r = cv.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = r.width; H = r.height;
      cv.width = W * dpr; cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // camera: world center (normalized) + zoom; eased toward target each frame
    const cam = { x: 0.35, y: 0.45, z: 1 };
    const camT = { x: 0.35, y: 0.45, z: 1 };
    const S = (p: { x: number; y: number }) => ({ x: (p.x - cam.x) * cam.z * W + W / 2, y: (p.y - cam.y) * cam.z * H + H / 2 });

    function draw() {
      const t = now();
      const ph = phaseRef.current;
      const b = bootRef.current;
      const lost = ph === "lost";
      const accent = lost ? "255, 107, 0" : "126, 224, 255";

      // camera targets by state
      if (ph === "found" || ph === "video") { camT.x = TARGET.x; camT.y = TARGET.y; camT.z = 2.6; }
      else if (ph === "lost") { camT.x = 0.35; camT.y = 0.45; camT.z = 1; }
      else if (b.stage < 2) { camT.x = 0.35 + 0.015 * Math.sin(t * 0.5); camT.y = 0.45; camT.z = 1.04; }
      else {
        // SCANNING TOUR: zoom into a town, hold, back out, next town
        const period = profile.dwell + profile.breathe;
        const scanT = t; // continuous
        const idx = Math.floor(scanT / period) % profile.tour.length;
        const within = scanT % period;
        const town = profile.tour[idx];
        if (within < profile.dwell) { camT.x = town.x; camT.y = town.y; camT.z = 2.3; }
        else { camT.x = 0.35; camT.y = 0.45; camT.z = 1; }
        // scan chip text (DOM, via ref — cheap)
        if (scanRef.current) {
          const checking = within < profile.dwell;
          scanRef.current.innerHTML = checking
            ? `SCANNING — ${town.n}<br><span class="dim">checking signal echoes…</span>`
            : `${town.n} — <span class="dim">no match. moving on.</span>`;
        }
        if (confRef.current) {
          const pct = Math.min(96, 42 + (scanT * 1.7) % 54);
          confRef.current.textContent = Math.floor(pct) + "%";
        }
      }
      cam.x += (camT.x - cam.x) * 0.055;
      cam.y += (camT.y - cam.y) * 0.055;
      cam.z += (camT.z - cam.z) * 0.055;

      ctx.clearRect(0, 0, W, H);

      // grid (world-anchored)
      ctx.strokeStyle = "rgba(126,224,255,0.05)"; ctx.lineWidth = 1;
      for (let i = -8; i < 24; i++) {
        const gx = S({ x: i / 16, y: 0 }).x;
        if (gx > -50 && gx < W + 50) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke(); }
        const gy = S({ x: 0, y: i / 10 }).y;
        if (gy > -50 && gy < H + 50) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke(); }
      }
      // lake
      const lakeA = S({ x: 0, y: 0.115 }), lakeB = S({ x: 0.22, y: 0.145 }), lakeC = S({ x: 0.42, y: 0.115 }), lakeD = S({ x: 0.6, y: 0.045 });
      ctx.beginPath(); ctx.moveTo(lakeA.x, lakeA.y); ctx.bezierCurveTo(lakeB.x, lakeB.y, lakeC.x, lakeC.y, lakeD.x, lakeD.y);
      ctx.strokeStyle = "rgba(126,224,255,0.32)"; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.fillStyle = "rgba(126,224,255,0.22)";
      ctx.font = `${Math.max(9, W * 0.008)}px ${MONO}`;
      const lakeLbl = S({ x: 0.14, y: 0.07 });
      ctx.fillText("L A K E   M I C H I G A N", lakeLbl.x, lakeLbl.y);
      // roads
      ctx.strokeStyle = "rgba(126,224,255,0.12)"; ctx.lineWidth = 1.4;
      const road = (pts: number[][]) => { ctx.beginPath(); pts.forEach((p, i) => { const q = S({ x: p[0], y: p[1] }); i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y); }); ctx.stroke(); };
      road([[0.19, 0.13], [0.19, 0.36], [0.24, 0.78]]);
      road([[0.06, 0.3], [0.385, 0.335], [0.62, 0.35]]);
      road([[0.385, 0.2], [0.385, 0.335], [0.41, 0.72]]);
      // towns
      const fpx = Math.max(9, W * 0.0082);
      ctx.font = `${fpx}px ${MONO}`;
      for (const town of TOWNS) {
        const p = S(town);
        if (p.x < -80 || p.x > W + 80 || p.y < -40 || p.y > H + 40) continue;
        const hot = town === ORIGIN || (town === TARGET && (ph === "found" || ph === "video"));
        ctx.fillStyle = hot ? `rgba(${accent},0.95)` : "rgba(126,224,255,0.4)";
        ctx.beginPath(); ctx.arc(p.x, p.y, hot ? 4 : 2.4, 0, 7); ctx.fill();
        ctx.fillStyle = hot ? `rgba(${accent},0.9)` : "rgba(126,224,255,0.32)";
        ctx.fillText(town.n, p.x + 8, p.y + 4);
      }
      const o = S(ORIGIN), g = S(TARGET);
      ctx.fillStyle = `rgba(${accent},0.7)`;
      ctx.fillText("ORIGIN — CHILD", o.x + 8, o.y + 4 + fpx * 1.3);

      // sonar pings from origin (world-scaled)
      for (let k = 0; k < 3; k++) {
        const r = (t * profile.pingRate + k * 0.33) % 1;
        ctx.beginPath(); ctx.arc(o.x, o.y, r * H * 0.7 * cam.z, 0, 7);
        ctx.strokeStyle = `rgba(${accent},${(1 - r) * 0.3})`; ctx.lineWidth = 1.4; ctx.stroke();
      }

      if (ph === "ring" && b.stage === 2) {
        // scanning reticle over the toured town when zoomed
        const period = profile.dwell + profile.breathe;
        const idx = Math.floor(t / period) % profile.tour.length;
        const within = t % period;
        if (within < profile.dwell) {
          const tp = S(profile.tour[idx]);
          const spin = t * 1.6;
          ctx.strokeStyle = `rgba(${accent},0.8)`; ctx.lineWidth = 1.8;
          ctx.beginPath(); ctx.arc(tp.x, tp.y, 30, spin, spin + 4.2); ctx.stroke();
          ctx.strokeStyle = `rgba(${accent},0.4)`;
          ctx.beginPath(); ctx.arc(tp.x, tp.y, 44, -spin * 0.7, -spin * 0.7 + 3.2); ctx.stroke();
          ctx.setLineDash([4, 6]);
          ctx.beginPath(); ctx.arc(tp.x, tp.y, 58 + 6 * Math.sin(t * 3), 0, 7); ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      if (ph === "found" || ph === "video") {
        const lt = t - foundAt;
        // leader from the child's town settles on Dad
        ctx.beginPath(); ctx.moveTo(o.x, o.y); ctx.lineTo(g.x, g.y);
        ctx.strokeStyle = "rgba(126,224,255,0.5)"; ctx.setLineDash([2, 6]); ctx.lineWidth = 1.6; ctx.stroke(); ctx.setLineDash([]);
        const spin = lt * 1.2;
        ctx.strokeStyle = "rgba(234,255,255,0.95)"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(g.x, g.y, 30, spin, spin + 4.6); ctx.stroke();
        ctx.strokeStyle = "rgba(126,224,255,0.6)";
        ctx.beginPath(); ctx.arc(g.x, g.y, 44, -spin, -spin + 3.6); ctx.stroke();
        ([[-1, 0], [1, 0], [0, -1], [0, 1]] as const).forEach(([dx, dy]) => {
          ctx.strokeStyle = "rgba(234,255,255,0.9)";
          ctx.beginPath(); ctx.moveTo(g.x + dx * 16, g.y + dy * 16); ctx.lineTo(g.x + dx * 54, g.y + dy * 54); ctx.stroke();
        });
        const pulse = 0.5 + 0.5 * Math.sin(lt * 6);
        ctx.fillStyle = `rgba(234,255,255,${0.6 + 0.4 * pulse})`;
        ctx.beginPath(); ctx.arc(g.x, g.y, 6, 0, 7); ctx.fill();
      }

      if (ph === "lost") {
        const blink = Math.sin(t * 5) > -0.2 ? 1 : 0.15;
        const bx = o.x + (g.x - o.x) * 0.55, by = o.y + (g.y - o.y) * 0.55;
        ctx.strokeStyle = `rgba(255,107,0,${0.75 * blink})`; ctx.lineWidth = 2; ctx.setLineDash([5, 5]);
        ctx.beginPath(); ctx.arc(bx, by, 30, 0, 7); ctx.stroke(); ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(bx - 12, by - 12); ctx.lineTo(bx + 12, by + 12);
        ctx.moveTo(bx + 12, by - 12); ctx.lineTo(bx - 12, by + 12); ctx.stroke();
        ctx.fillStyle = `rgba(255,107,0,${0.85 * blink})`;
        ctx.fillText("LAST KNOWN BEARING", bx + 40, by + 4);
      }

      raf = requestAnimationFrame(draw);
    }
    raf = requestAnimationFrame(draw);

    return () => { window.removeEventListener("resize", resize); cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanning = phase === "ring" && boot.stage === 2;
  const head =
    phase === "found" ? "DAD FOUND" : phase === "video" ? "DAD FOUND" : phase === "lost" ? "DAD NOT FOUND"
    : boot.stage < 2 ? "DAD LINK" : "LOCATING DAD";
  const sub =
    phase === "found" || phase === "video" ? "VOICE LINK ACTIVE — TALK NORMALLY"
    : phase === "lost" ? "NO ANSWER — A MESSAGE WAS SENT. DAD WILL CALL BACK."
    : boot.stage < 2 ? "SECURE CHANNEL BOOT SEQUENCE" : "SWEEPING THE REGION";

  return (
    <div className={`dadlink-overlay ${phase === "lost" ? "lost" : ""}`}>
      <style>{CSS}</style>
      <canvas ref={canvasRef} />
      <div className={`dl-head ${phase === "found" ? "blink" : ""}`}>{head}</div>
      <div className="dl-sub">{sub}</div>

      {phase === "ring" && boot.stage === 0 && (
        <div className="dl-boot">
          <h3>STEP 1 — ENCRYPTED LINK</h3>
          <div className="line done">Secure channel opened from this console</div>
          <div className="line active">Connecting to the cell tower network…</div>
          <div className="line active">Masking our signal — avoiding detection</div>
          <div className="dl-bar"><i style={{ width: `${boot.pct}%` }} /></div>
        </div>
      )}
      {phase === "ring" && boot.stage === 1 && (
        <div className="dl-boot">
          <h3>STEP 2 — SATELLITE ACCESS QUEUE</h3>
          {SATS.map((s, i) => (
            <div key={s.name} className={`dl-sat ${boot.sats[i] === "linked" ? "linked" : boot.sats[i] === "req" ? "req" : ""}`}>
              <span>{s.name} <span style={{ opacity: 0.5 }}>· {s.detail}</span></span>
              <span className="st">{boot.sats[i] === "linked" ? "LINKED" : boot.sats[i] === "req" ? "REQUESTING…" : "IN QUEUE"}</span>
            </div>
          ))}
        </div>
      )}

      {scanning && <div className="dl-scan" ref={scanRef} />}
      {scanning && (
        <div className="dl-conf"><div className="n" ref={confRef}>42%</div><div className="l">SIGNAL CONFIDENCE</div></div>
      )}
      {phase === "ring" && (
        <div className="dl-ring-stage">
          <div className="dl-chip on">DESKTOP — RINGING</div>
          <div className={`dl-chip ${ringStage === "phone" ? "on" : ""}`}>{ringStage === "phone" ? "PHONE — RINGING" : "PHONE — STANDBY"}</div>
        </div>
      )}
      {phase === "lost" && (
        <div className="dl-beacon"><i /><span>CALLBACK BEACON ACTIVE — DAD HAS BEEN NOTIFIED</span></div>
      )}
      {(phase === "found" || phase === "video") && (
        <div className="dl-timer">{Math.floor(callSecs / 60)}:{String(callSecs % 60).padStart(2, "0")}</div>
      )}

      <video style={{ position: "absolute", width: 1, height: 1, opacity: 0 }} ref={remoteRef} autoPlay playsInline />
      <button className="dl-end" onClick={onClose}>END LINK</button>
    </div>
  );
}
